import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useActionTracking, useItem, usePurchasePlaces, useSubmitAuthenticationAskInfo } from '@/hooks/item';
import { ItemAskMoreInfoModal } from '@/types/modal';
import { DatePicker, Dropdown } from '@/components/molecules/common/Form';
import { StandardModal } from '@/components/molecules/common/Modal';
import styles from '../Modal.module.scss';
import clsx from 'clsx';
import UploadButton from './UploadButton';
import { SubmitAuthenticationAskInfoPayload } from '@/services/item';
import { Typography } from '@mui/material';
import { useErrorHandling } from '@/hooks/common';

const AskMoreInfo: React.FC<{ modal: ItemAskMoreInfoModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const currentTime = dayjs(new Date());

  const [dueDate, setDueDate] = useState<dayjs.Dayjs | null>(null);
  const [location, setLocation] = useState('');

  const [file, setFile] = useState<File | null>(null);

  const { getErrorMsgFromResponse } = useErrorHandling();

  const { mutate, isLoading: isSubmitting, error } = useSubmitAuthenticationAskInfo(item.productId);

  const { data: purchasePlaces = [], isLoading: locationLoading } = usePurchasePlaces();

  const { refetch: refetchCurrentItem } = useItem(item.productId, false);

  const { mutate: tracking } = useActionTracking(item.productId);

  const locations: { label: string; value: string }[] = useMemo(() => {
    if (!purchasePlaces.length) return [];
    return purchasePlaces.map((l) => ({ label: l.name, value: l.id.toString() }));
  }, [purchasePlaces]);

  const handleSubmit = async () => {
    const payload: SubmitAuthenticationAskInfoPayload = {
      file,
      location,
      date: dueDate?.toISOString(),
    };

    mutate(payload, {
      onSuccess: () => {
        tracking('upload_seller_invoice');
        refetchCurrentItem();
        onClose();
      },
    });
  };

  const isDisabled = useMemo(() => {
    if (location || dueDate) {
      return !!location && !!dueDate && dayjs(dueDate).isValid();
    }
    return !!file;
  }, [location, dueDate, file]);

  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('modal.item_ask_more_info.item_no')} #${item.productId}`}</div>

      <div
        className={clsx(styles.text0875rem, styles.askMoreInfo_description)}
        dangerouslySetInnerHTML={{ __html: t('modal.item_ask_more_info.description') }}
      />

      <UploadButton
        file={file}
        setFile={setFile}
      />

      <Dropdown
        disabled={locationLoading}
        label={t('modal.item_ask_more_info.location')}
        value={location}
        onSelect={setLocation}
        items={locations}
      />

      <DatePicker
        disabled={false}
        label={t('modal.item_ask_more_info.date')}
        max={currentTime}
        onChange={setDueDate}
        value={dueDate || undefined}
      />

      {!!error && (
        <Typography
          color="error"
          variant="body2"
        >
          {getErrorMsgFromResponse(error)}
        </Typography>
      )}
    </div>
  );

  return (
    <StandardModal
      title={t('modal.item_ask_more_info.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={t('common.submit').toUpperCase()}
      submittable={isDisabled && !isSubmitting}
      body={body}
      containerStyle={{ width: 500 }}
      modalName="item-ask-more-info"
      disableBackdropClose={isSubmitting}
    />
  );
};

export default AskMoreInfo;
